import { useState, useEffect } from 'react';
import { 
  MatchplayTournament, 
  MatchplayPlayer, 
  MatchplayMatch,
  CreateTournamentRequest,
  CreatePlayerRequest 
} from '../../types/matchplay-v2';
import { MatchplayDatabase } from '../../lib/matchplay-v2/database';
import { MatchplayRealtime } from '../../lib/matchplay-v2/realtime';
import { MatchplayScoring } from '../../lib/matchplay-v2/scoring';

export function useMatchplayTournament(tournamentId?: string) {
  const [tournament, setTournament] = useState<MatchplayTournament | null>(null);
  const [players, setPlayers] = useState<MatchplayPlayer[]>([]);
  const [matches, setMatches] = useState<MatchplayMatch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load tournament data
  useEffect(() => {
    if (!tournamentId) return;

    const loadTournament = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [tournamentData, playersData, matchesData] = await Promise.all([
          MatchplayDatabase.getTournament(tournamentId),
          MatchplayDatabase.getPlayers(tournamentId),
          MatchplayDatabase.getMatches(tournamentId)
        ]);

        // Check for format mismatch between tournament and matches
        if (matchesData.length > 0 && tournamentData.format !== matchesData[0].format) {
          console.warn(`Format mismatch detected: Tournament shows '${tournamentData.format}' but matches use '${matchesData[0].format}'. Using match format as source of truth.`);
          
          // Update tournament format to match the actual matches
          try {
            await MatchplayDatabase.updateTournamentFormat(tournamentId, matchesData[0].format);
            // Update local state with corrected format
            tournamentData.format = matchesData[0].format;
          } catch (error) {
            console.error('Failed to fix tournament format mismatch:', error);
          }
        }

        setTournament(tournamentData);
        setPlayers(playersData);
        setMatches(matchesData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load tournament');
      } finally {
        setIsLoading(false);
      }
    };

    loadTournament();
  }, [tournamentId]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!tournamentId) return;

    const unsubscribe = MatchplayRealtime.subscribeToTournament(tournamentId, {
      onTournamentUpdate: (updatedTournament) => {
        setTournament(updatedTournament);
      },
      onPlayerUpdate: (updatedPlayer) => {
        setPlayers(prev => prev.map(p => p.id === updatedPlayer.id ? updatedPlayer : p));
      },
      onMatchUpdate: (updatedMatch) => {
        setMatches(prev => prev.map(m => m.id === updatedMatch.id ? updatedMatch : m));
      }
    });

    return unsubscribe;
  }, [tournamentId]);

  // Tournament operations
  const createTournament = async (request: CreateTournamentRequest): Promise<MatchplayTournament> => {
    setIsLoading(true);
    setError(null);

    try {
      const newTournament = await MatchplayDatabase.createTournament(request);
      setTournament(newTournament);
      return newTournament;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create tournament';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const addPlayers = async (playerRequests: CreatePlayerRequest[]): Promise<void> => {
    if (!tournament) throw new Error('No tournament selected');

    setIsLoading(true);
    setError(null);

    try {
      const newPlayers = await MatchplayDatabase.addPlayers(tournament.id, playerRequests);
      setPlayers(prev => [...prev, ...newPlayers]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add players';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const generateMatches = async (): Promise<void> => {
    if (!tournament) throw new Error('No tournament selected');

    setIsLoading(true);
    setError(null);

    try {
      const newMatches = await MatchplayDatabase.generateMatches(tournament.id, tournament.format);
      setMatches(newMatches);
      
      // Update tournament status to active
      await MatchplayDatabase.updateTournamentStatus(tournament.id, 'active');
      setTournament(prev => prev ? { ...prev, status: 'active' } : null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate matches';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const updateTournamentStatus = async (status: 'setup' | 'active' | 'completed'): Promise<void> => {
    if (!tournament) throw new Error('No tournament selected');

    setIsLoading(true);
    setError(null);

    try {
      await MatchplayDatabase.updateTournamentStatus(tournament.id, status);
      setTournament(prev => prev ? { ...prev, status } : null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update tournament status';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper functions
  const getActiveMatches = () => {
    return matches.filter(m => {
      if (!Array.isArray(m.holes)) return true; // No holes played yet = active
      const statusCalc = MatchplayScoring.calculateMatchStatus(m);
      return statusCalc.status !== 'completed';
    });
  };

  const getCompletedMatches = () => {
    return matches.filter(m => {
      if (!Array.isArray(m.holes)) return false; // No holes played = not completed
      const statusCalc = MatchplayScoring.calculateMatchStatus(m);
      return statusCalc.status === 'completed';
    });
  };

  const getTournamentProgress = () => {
    if (matches.length === 0) return 0;
    return Math.round((getCompletedMatches().length / matches.length) * 100);
  };

  const getTeamStats = () => {
    if (!tournament || tournament.format === 'singles') {
      return {
        totalPlayers: players.length,
        format: tournament?.format || 'singles'
      };
    }

    const teamA = players.filter(p => p.teamAssignment === 'A');
    const teamB = players.filter(p => p.teamAssignment === 'B');

    return {
      totalPlayers: players.length,
      teamA: teamA.length,
      teamB: teamB.length,
      format: tournament.format
    };
  };

  return {
    // State
    tournament,
    players,
    matches,
    isLoading,
    error,

    // Actions
    createTournament,
    addPlayers,
    generateMatches,
    updateTournamentStatus,

    // Helpers
    getActiveMatches,
    getCompletedMatches,
    getTournamentProgress,
    getTeamStats,

    // Computed values
    activeMatches: getActiveMatches(),
    completedMatches: getCompletedMatches(),
    tournamentProgress: getTournamentProgress(),
    teamStats: getTeamStats()
  };
} 